import React, {useCallback} from 'react';
import {Platform, Pressable, StyleSheet} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import {useNavigationState} from '@react-navigation/native';
import type {NavigationState, PartialState} from '@react-navigation/routers';

import {
  type ScrollableRef,
  useScrollCollapseContext,
  useScrollRefContext,
} from '../context/ScrollCollapseContext';
import {AppRoute} from '../models/AppRoute';
import theme from '../style/theme';

import {Icon} from './Icon';

const SHOW_THRESHOLD = 300;

const SHOW_CONFIG = {duration: 200, easing: Easing.out(Easing.cubic)};
const HIDE_CONFIG = {duration: 150, easing: Easing.out(Easing.cubic)};

// Routes where the scroll-to-top button adds no value on native mobile —
// detail screens (single long scroll, users tap status bar to scroll to top
// on iOS, and swipe-back is fast on both platforms). Suppressed on iOS/Android
// only; kept on web where the native scroll-to-top gesture doesn't exist.
const NATIVE_SUPPRESSED_ROUTES = new Set<string>([
  AppRoute.PRODUCT,
  AppRoute.ARTICLE,
  AppRoute.EXCLUSIVE_OFFER_DETAIL,
  AppRoute.PROMOTION,
]);

// Walk the React Navigation state tree down to the currently-focused leaf
// route. ScrollToTopButton lives at the Tab/Stack navigator root, so the
// top-level state's focused index may itself contain a nested state.
function getFocusedRouteName(
  state: NavigationState | PartialState<NavigationState> | undefined,
): string | undefined {
  if (!state || !state.routes || state.routes.length === 0) return undefined;
  const index = state.index ?? 0;
  const route = state.routes[index];
  if (route?.state) return getFocusedRouteName(route.state);
  return route?.name;
}

const ScrollToTopButton = () => {
  const {scrollY} = useScrollCollapseContext();
  const {activeScrollRef} = useScrollRefContext();
  const focusedRouteName = useNavigationState(getFocusedRouteName);

  // Return null BEFORE hooks below? No — keep hook order stable. Use the flag
  // inside the derived-value / animated-style logic to force hidden state on
  // suppressed routes. Platform.OS is constant per process, so this only
  // pays the cost on native mobile.
  const isSuppressed =
    Platform.OS !== 'web' && !!focusedRouteName && NATIVE_SUPPRESSED_ROUTES.has(focusedRouteName);

  const isVisible = useDerivedValue(() => !isSuppressed && scrollY.get() > SHOW_THRESHOLD);

  const animatedStyle = useAnimatedStyle(() => {
    const visible = isVisible.get();
    const config = visible ? SHOW_CONFIG : HIDE_CONFIG;

    return {
      opacity: withTiming(visible ? 0.92 : 0, config),
      transform: [
        {scale: withTiming(visible ? 1 : 0.6, config)},
        {translateY: withTiming(visible ? 0 : 10, config)},
      ],
      pointerEvents: visible ? 'auto' : 'none',
    };
  });

  const handlePress = useCallback(() => {
    // Dereference: activeScrollRef → consumer's ref object → the actual scrollable
    const ref = activeScrollRef.current?.current as ScrollableRef;
    if (!ref) return;

    // FlatList / FlashList
    if (typeof ref.scrollToOffset === 'function') {
      ref.scrollToOffset({offset: 0, animated: true});
      // ScrollView / Animated.ScrollView
    } else if (typeof ref.scrollTo === 'function') {
      ref.scrollTo({x: 0, y: 0, animated: true});
      // FlatList scrollToIndex fallback
    } else if (typeof ref.scrollToIndex === 'function') {
      ref.scrollToIndex({index: 0, animated: true});
    }
  }, [activeScrollRef]);

  return (
    <Animated.View style={[styles.wrapper, animatedStyle]} testID="scroll-to-top-button">
      <Pressable
        onPress={handlePress}
        hitSlop={12}
        style={styles.touchable}
        testID="scroll-to-top-pressable">
        <Icon name="chevron-up" size={14} color="white" />
      </Pressable>
    </Animated.View>
  );
};

export default ScrollToTopButton;

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    bottom: 72,
    right: 16,
  },
  touchable: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colour.uiControls.primary,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
});
